import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ActionIcon,
  Box,
  Grid,
  Group,
  Text,
  Button,
  Loader,
  Alert,
  Stack,
  Paper,
  Badge,
  Card,
  Divider,
  Tooltip,
  Modal,
  TextInput,
  Progress,
  Anchor,
  Collapse,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCheck,
  IconArrowLeft,
  IconAlertTriangle,
  IconBarcode,
  IconChevronDown,
  IconChevronRight,
  IconEdit,
  IconPlayerTrackNext,
  IconLockOpen,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useDisclosure } from "@mantine/hooks";
import { useAuth } from "../../hooks/useAuth";
import { SpecimenInsufficientTissueCheckbox } from "../../components/grossing/SpecimenInsufficientTissueCheckbox";
import { useHasPermission } from "../../hooks/useHasPermission";
import {
  useCaseForEmbedding,
  useStartEmbedding,
  useCompleteEmbedding,
  useCaseLock,
  useReleaseLock,
  useExtendLock,
  useForceReleaseLock,
  useEmbeddingQueue,
  useMarkBlockAsEmbedded,
  useOpenCaseByBarcode,
} from "../../hooks/useWorkflows";
import {
  GrossingCaseSummary,
  GrossDescriptionEditorModal,
  GrossingRequisitionCard,
} from "../../components/grossing";
import { CaseNotesSection } from "../../components/accessioning/CaseNotesSection";
import { CaseFlagsSection } from "../../components/cases/CaseFlagsSection";
import { CaseCommentsSection } from "../../components/accessioning/CaseCommentsSection";
import ErrorsCard from "../../components/accessioning/ErrorsCard";
import {
  isCompleteCassetteBarcode,
  useBarcodeScanner,
} from "../../hooks/useBarcodeScanner";
import { extractFragmentCountFromDescription } from "../../utils/extractFragmentCount";
import {
  StickerPrinterStatusPanel,
  ReceivedExternalPassThroughPanel,
} from "../../components/sticker";
import { useStickerPrinterStatus } from "../../hooks/useStickerPrinterStatus";
import {
  stickerLabelPrinterService,
  type StickerLabelData,
} from "../../services/stickerLabelPrinter.service";
import { workstationPreferenceService } from "../../services/workstationPreference.service";
import type { CaseBlock, CaseSpecimen } from "../../services/cases.service";
import { resolvePatientLabelName } from "../../utils/patientLabelName";
import { getFragmentCountLabel } from "../../utils/fragmentCount";
import { shouldShowLegacyScannerControls } from "../../utils/scannerControls";
import { requireDateOnlyString } from "../../utils/dateOnly";

interface EmbeddingBlockStickerLabelInput {
  block: CaseBlock;
  caseId: string;
  caseNumber: string;
  dateReceived: unknown;
  patientName: string;
  specimenNumber: string;
}

export function buildEmbeddingBlockStickerLabel({
  block,
  caseId,
  caseNumber,
  dateReceived,
  patientName,
  specimenNumber,
}: EmbeddingBlockStickerLabelInput): StickerLabelData {
  const blockNumber = block.number ?? "";
  return {
    itemSlotId: `${Date.now()}-b-${block.id}`,
    itemType: "block",
    caseId,
    itemId: block.id,
    caseNumber,
    patientName,
    specimen: specimenNumber,
    blockId: blockNumber,
    slideId: "",
    stain: "",
    dateReceived: requireDateOnlyString(dateReceived, "Date received"),
    barcodeValue: `${caseNumber};${specimenNumber};${blockNumber}`,
    isReprint: true,
  };
}

function resolveGrossVariables(html: string): string {
  if (!html) return "";
  return html
    .replace(
      /<span[^>]*data-variable-value="([^"]*)"[^>]*>\{\{\w+\}\}<\/span>/g,
      "$1"
    )
    .replace(/<span[^>]*data-variable="[^"]*"[^>]*>\{\{\w+\}\}<\/span>/g, "")
    .replace(/\{\{[^}]+\}\}/g, "");
}

export default function EmbeddingDetailPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const showLegacyScannerControls = shouldShowLegacyScannerControls();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const userId = user?.id;

  // Get scanned block info from URL params
  const scannedSpec = searchParams.get("scannedSpec");
  const scannedBlock = searchParams.get("scannedBlock");
  const scannedBlockKey =
    scannedSpec && scannedBlock ? `${scannedSpec}-${scannedBlock}` : null;

  // State
  const [
    cancelModalOpened,
    { open: openCancelModal, close: closeCancelModal },
  ] = useDisclosure(false);
  const highlightedSpecimenRef = useRef<HTMLDivElement>(null);

  // Block scanning state
  const [scannedBlocks, setScannedBlocks] = useState<Set<string>>(new Set());
  const [scanInput, setScanInput] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const scanInputRef = useRef<HTMLInputElement>(null);

  // Gross description collapse state (expanded by default)
  const [expandedGross, setExpandedGross] = useState<Set<string>>(new Set());

  // Gross description editor modal state
  const [editingSpecimen, setEditingSpecimen] = useState<any>(null);
  const [
    grossEditorOpened,
    { open: openGrossEditor, close: closeGrossEditor },
  ] = useDisclosure(false);

  // Left sidebar scroll indicator state
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  // Track if we've already started embedding to prevent infinite loops
  const hasStartedEmbedding = useRef(false);

  // Reset the flag when navigating to a different case
  useEffect(() => {
    hasStartedEmbedding.current = false;
  }, [caseId]);

  // Queries
  const {
    data: caseData,
    isLoading: caseLoading,
    error: caseError,
    refetch: refetchCase,
  } = useCaseForEmbedding(caseId);

  // Lock query
  const { data: lockData } = useCaseLock(caseId);

  // Embedding queue for navigation and "Complete & Next" functionality
  const { data: queueCases = [] } = useEmbeddingQueue();

  // Mutations
  const startEmbeddingMutation = useStartEmbedding();
  const completeEmbeddingMutation = useCompleteEmbedding();
  const releaseLockMutation = useReleaseLock();
  const extendLockMutation = useExtendLock();
  const markBlockAsEmbeddedMutation = useMarkBlockAsEmbedded();
  const openCaseByBarcodeMutation = useOpenCaseByBarcode();

  // Check if locked by another user
  const isLockedByOther =
    lockData && lockData.lockedById && lockData.lockedById !== userId;

  const canForceRelease = useHasPermission("case_locks.force_release");
  const forceReleaseMutation = useForceReleaseLock();

  const { data: stickerPrinterStatus } = useStickerPrinterStatus();

  const handleReprintSticker = useCallback(
    async (block: CaseBlock) => {
      const workstationId =
        workstationPreferenceService.getSelectedWorkstationId();
      if (!workstationId || !stickerPrinterStatus?.deviceId) {
        notifications.show({
          title: "Print Failed",
          message: "Printer or workstation not ready.",
          color: "red",
          autoClose: 4000,
        });
        return;
      }

      if (stickerPrinterStatus.status !== "connected") {
        notifications.show({
          title: "Print Failed",
          message: "Printer is offline or in error state.",
          color: "red",
          autoClose: 4000,
        });
        return;
      }

      const specimen = (caseData?.specimens || []).find(
        (s: CaseSpecimen) => s.id === block.specimenId
      );
      if (!caseData || !specimen) {
        notifications.show({
          title: "Print Failed",
          message: "Case data not ready.",
          color: "red",
          autoClose: 4000,
        });
        return;
      }

      const patientName =
        resolvePatientLabelName(caseData.patient, caseData.draftPatientData) ??
        "";
      const specimenNum = specimen.number ?? "";

      try {
        const label = buildEmbeddingBlockStickerLabel({
          block,
          caseId: caseId!,
          caseNumber: caseData.caseNumber,
          dateReceived: caseData.dateReceived,
          patientName,
          specimenNumber: specimenNum,
        });
        const result = await stickerLabelPrinterService.printStickerLabels(
          [label],
          stickerPrinterStatus.deviceId,
          workstationId
        );
        if (result.success) {
          notifications.show({
            title: "Reprint Dispatched",
            message: `Sticker for ${specimenNum}${label.blockId} sent via ${result.method} bridge.`,
            color: "green",
            autoClose: 4000,
          });
        } else {
          notifications.show({
            title: "Reprint Failed",
            message: result.error || "Could not send sticker to printer.",
            color: "red",
            autoClose: 5000,
          });
        }
      } catch (err: any) {
        notifications.show({
          title: "Reprint Failed",
          message: err.message || "An unexpected error occurred.",
          color: "red",
          autoClose: 5000,
        });
      }
    },
    [caseId, caseData, stickerPrinterStatus]
  );

  const handleForceUnlock = useCallback(() => {
    if (
      !window.confirm(
        `Force-unlock this case from ${
          lockData?.lockedByName || "another user"
        }? They will lose their lock immediately.`
      )
    )
      return;
    forceReleaseMutation.mutate(caseId!, {
      onSuccess: () => {
        notifications.show({
          title: "Lock Released",
          message: "The case has been unlocked.",
          color: "green",
        });
      },
      onError: (err: Error) => {
        notifications.show({
          title: "Error",
          message: err.message || "Failed to release lock",
          color: "red",
        });
      },
    });
  }, [caseId, lockData, forceReleaseMutation]);

  // Auto-start embedding on mount (only if not locked by another user and not blocked)
  useEffect(() => {
    if (hasStartedEmbedding.current) return;

    // Skip auto-start for blocked cases or cases that have already completed embedding
    if (
      caseId &&
      userId &&
      caseData &&
      !caseData.isBlocked &&
      !caseData.embeddingCompletedAt &&
      !lockData &&
      !isLockedByOther
    ) {
      hasStartedEmbedding.current = true;
      startEmbeddingMutation.mutate(caseId, {
        onError: (error: any) => {
          hasStartedEmbedding.current = false;
          notifications.show({
            title: "Error",
            message: error.message || "Failed to start embedding",
            color: "red",
          });
          navigate("/embedding");
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, userId, caseData, lockData, isLockedByOther]);

  // Extend lock every 5 minutes
  useEffect(() => {
    if (!caseId || !userId || !lockData || isLockedByOther) return;

    const interval = setInterval(() => {
      extendLockMutation.mutate(caseId);
    }, 2 * 60 * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, userId, lockData, isLockedByOther]);

  // Mirrors scannedBlocks state but updated synchronously (before React re-renders).
  // Used in barcode callbacks to avoid stale-closure reads of React state.
  const scannedBlocksRef = useRef<Set<string>>(new Set());

  const getMatchingBlocks = useCallback(
    (specimenNumber: string, blockNumber: string) =>
      (caseData?.specimens || []).flatMap((specimen: any) =>
        specimen.number === specimenNumber
          ? (specimen.blocks || []).filter(
              (block: any) => block.number === blockNumber
            )
          : []
      ),
    [caseData]
  );

  const markBlockScannedLocally = useCallback((blockId: string) => {
    scannedBlocksRef.current = new Set([
      ...Array.from(scannedBlocksRef.current),
      blockId,
    ]);
    setScannedBlocks((prev) => new Set([...Array.from(prev), blockId]));
  }, []);

  // Initialize scanned blocks from backend data (blocks with embeddedAt)
  useEffect(() => {
    if (!caseData) return;

    const embeddedBlocks: string[] = [];

    // Load already embedded blocks from the case data
    caseData.specimens?.forEach((specimen: any) => {
      specimen.blocks?.forEach((block: any) => {
        if (block.embeddedAt && block.id) {
          embeddedBlocks.push(block.id);
        }
      });
    });

    scannedBlocksRef.current = new Set(embeddedBlocks);
    setScannedBlocks(new Set(embeddedBlocks));

    // Expand all gross descriptions by default
    const specimenIds = (caseData.specimens || [])
      .filter((s: any) => s.gross)
      .map((s: any) => s.id);
    if (specimenIds.length > 0) {
      setExpandedGross(new Set(specimenIds));
    }
  }, [caseData]);

  // Auto-mark block as embedded when scanned from queue page
  const hasAutoMarkedBlock = useRef(false);
  // Updated synchronously before each await so cross-case checks see the correct count
  // even when a mutation fires just before the cross-case scan arrives.
  const pendingScannedBlocksRef = useRef<Set<string>>(new Set());
  const pendingMutationPromisesRef = useRef<Promise<any>[]>([]);
  // Guards against double-firing auto-complete (e.g. after a scan triggers
  // completion, an immediate re-render or stray event shouldn't re-call it).
  const autoCompletedRef = useRef(false);

  // Reset when navigating to a different case (component does not remount with React Router)
  useEffect(() => {
    hasAutoMarkedBlock.current = false;
    scannedBlocksRef.current = new Set();
    pendingScannedBlocksRef.current = new Set();
    pendingMutationPromisesRef.current = [];
    autoCompletedRef.current = false;
  }, [caseId]);

  useEffect(() => {
    if (
      !caseId ||
      !scannedSpec ||
      !scannedBlock ||
      !caseData ||
      caseData.isBlocked ||
      hasAutoMarkedBlock.current
    ) {
      return;
    }

    const blockKey = `${scannedSpec}-${scannedBlock}`;
    const matchingBlocks = getMatchingBlocks(scannedSpec, scannedBlock);

    // Check if block is already embedded
    const isAlreadyEmbedded =
      matchingBlocks.length === 0 ||
      matchingBlocks.every((block: any) => block.embeddedAt);

    if (isAlreadyEmbedded) {
      return;
    }

    // Mark the block as embedded via API
    hasAutoMarkedBlock.current = true;
    markBlockAsEmbeddedMutation.mutate(
      {
        caseId,
        specimenNumber: scannedSpec,
        blockNumber: scannedBlock,
      },
      {
        onSuccess: (result) => {
          const blockIdentity =
            result.block?.id ??
            matchingBlocks.find((block: any) => !block.embeddedAt)?.id;
          if (blockIdentity) {
            markBlockScannedLocally(blockIdentity);
          }
          if (!result.block?.alreadyEmbedded) {
            notifications.show({
              title: "Block Scanned",
              message: `Block ${blockKey} marked as embedded`,
              color: "green",
            });
          }
          if (result.embeddingCompleted) {
            autoCompletedRef.current = true;
            notifications.show({
              title: "Embedding Complete",
              message: "All blocks embedded — case moved to Pending Cutting",
              color: "green",
            });
          }
        },
        onError: (error: any) => {
          hasAutoMarkedBlock.current = false;
          notifications.show({
            title: "Error",
            message: error.message || "Failed to mark block as embedded",
            color: "red",
          });
        },
      }
    );
  }, [
    caseId,
    scannedSpec,
    scannedBlock,
    caseData,
    markBlockAsEmbeddedMutation,
    getMatchingBlocks,
    markBlockScannedLocally,
  ]);

  // Auto-scroll to highlighted specimen when page loads with scanned block
  useEffect(() => {
    if (scannedBlockKey && highlightedSpecimenRef.current) {
      setTimeout(() => {
        highlightedSpecimenRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 500);
    }
  }, [scannedBlockKey, caseData]);

  // Check if left sidebar needs scroll indicator
  useEffect(() => {
    const checkScrollable = () => {
      const scrollContainer = document.querySelector(".left-sidebar-scroll");
      if (scrollContainer) {
        const hasScroll =
          scrollContainer.scrollHeight > scrollContainer.clientHeight;
        const isAtBottom =
          scrollContainer.scrollTop + scrollContainer.clientHeight >=
          scrollContainer.scrollHeight - 10;
        setShowScrollIndicator(hasScroll && !isAtBottom);
      }
    };

    // Check initially and on scroll/resize
    checkScrollable();
    const scrollContainer = document.querySelector(".left-sidebar-scroll");
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", checkScrollable);
    }
    window.addEventListener("resize", checkScrollable);

    // Re-check periodically for content changes (e.g., collapsing cards)
    const interval = setInterval(checkScrollable, 500);

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", checkScrollable);
      }
      window.removeEventListener("resize", checkScrollable);
      clearInterval(interval);
    };
  }, []);

  // Release lock on unmount
  useEffect(() => {
    return () => {
      if (caseId) {
        releaseLockMutation.mutate(caseId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // Queue navigation - find current position and next/previous cases
  const availableCases = queueCases.filter((c: any) => !c.isBlocked);
  const currentAvailableIndex = availableCases.findIndex(
    (c: any) => c.id === caseId
  );

  const hasPreviousCase = currentAvailableIndex > 0;
  const hasNextCase = currentAvailableIndex < availableCases.length - 1;

  const previousCase = hasPreviousCase
    ? availableCases[currentAvailableIndex - 1]
    : null;
  const nextCase = hasNextCase
    ? availableCases[currentAvailableIndex + 1]
    : null;

  // Navigation handlers
  const handlePreviousCase = () => {
    if (previousCase) {
      navigate(`/embedding/${previousCase.id}`);
    }
  };

  const handleNextCase = () => {
    if (nextCase) {
      navigate(`/embedding/${nextCase.id}`);
    }
  };

  // Handlers
  const handleCompleteEmbedding = async () => {
    if (!caseId || !userId) return;

    try {
      await completeEmbeddingMutation.mutateAsync(caseId);
      notifications.show({
        title: "Embedding Complete",
        message: "Case has been moved to the next workflow stage",
        color: "green",
      });
      navigate("/embedding");
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to complete embedding",
        color: "red",
      });
    }
  };

  const handleCompleteAndNext = async () => {
    if (!caseId || !userId) return;

    try {
      await completeEmbeddingMutation.mutateAsync(caseId);
      notifications.show({
        title: "Embedding Complete",
        message: "Case has been moved to the next workflow stage",
        color: "green",
      });

      // Navigate to next case if available
      if (nextCase) {
        navigate(`/embedding/${nextCase.id}`);
      } else {
        notifications.show({
          title: "Queue Empty",
          message: "No more cases in the embedding queue",
          color: "blue",
        });
        navigate("/embedding");
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to complete embedding",
        color: "red",
      });
    }
  };

  const handleCancel = async () => {
    closeCancelModal();
    if (caseId) {
      await releaseLockMutation.mutateAsync(caseId);
    }
    navigate("/embedding");
  };

  // Auto-complete embedding when every block has been scanned. Shared by
  // the keyboard, hardware-scanner, and cross-case scan paths.
  const maybeAutoCompleteEmbedding = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!caseId || caseData?.isBlocked) return false;
      if (caseData?.embeddingCompletedAt || autoCompletedRef.current)
        return true;

      const currentTotalBlocks = (caseData?.specimens || []).reduce(
        (acc: number, s: any) => acc + Number(s.blocks?.length ?? 0),
        0
      );
      if (currentTotalBlocks === 0) return false;

      const effectiveScannedSize =
        scannedBlocksRef.current.size + pendingScannedBlocksRef.current.size;
      if (effectiveScannedSize < currentTotalBlocks) return false;

      autoCompletedRef.current = true;
      try {
        // Wait for any in-flight block scans so the backend sees every block
        // as embedded before completion runs.
        const pendingResults = await Promise.allSettled([
          ...pendingMutationPromisesRef.current,
        ]);
        if (pendingResults.some((result) => result.status === "rejected")) {
          autoCompletedRef.current = false;
          return false;
        }
        await completeEmbeddingMutation.mutateAsync(caseId);
        if (!options?.silent) {
          notifications.show({
            title: "Embedding Complete",
            message: "All blocks scanned — case moved to Pending Cutting",
            color: "green",
          });
        }
        return true;
      } catch (completeErr: any) {
        autoCompletedRef.current = false;
        notifications.show({
          title: "Warning",
          message: `Could not auto-complete embedding for current case: ${
            completeErr.message || "Unknown error"
          }`,
          color: "yellow",
        });
        return false;
      }
    },
    [caseId, caseData, completeEmbeddingMutation]
  );

  // Handle block barcode scan
  const handleBlockScan = async (e: React.KeyboardEvent) => {
    if (e.key !== "Enter" || !scanInput.trim()) return;
    if (!caseId) return;

    setScanError(null);

    // Parse barcode format: {caseNumber};{specimenLetter};{blockNumber}
    const parts = scanInput.trim().split(";");
    if (parts.length !== 3) {
      setScanError(
        "Invalid barcode format. Expected: {caseNumber};{specimenLetter};{blockNumber}"
      );
      setScanInput("");
      return;
    }

    const [scannedCaseNumber, specLetter, blockNum] = parts;
    const blockKey = `${specLetter}-${blockNum}`;
    const matchingBlocks = getMatchingBlocks(specLetter, blockNum);

    // If barcode belongs to a different case, auto-complete current case (if done) and navigate there
    if (
      caseData &&
      scannedCaseNumber.toUpperCase() !== caseData.caseNumber.toUpperCase()
    ) {
      const capturedInput = scanInput.trim();
      setScanInput("");
      try {
        const completed = await maybeAutoCompleteEmbedding({ silent: true });
        if (!completed) {
          setScanError(
            "Finish scanning every block before opening another case"
          );
          return;
        }
        const caseDataRes = await openCaseByBarcodeMutation.mutateAsync({
          barcode: capturedInput,
          intent: "embedding",
        });
        navigate(
          `/embedding/${caseDataRes.id}?scannedSpec=${specLetter}&scannedBlock=${blockNum}`
        );
      } catch (err: any) {
        setScanError(err.message || "Failed to open case");
      }
      return;
    }

    // Verify this block exists in the case
    const blockExists = matchingBlocks.length > 0;

    if (!blockExists) {
      setScanError(`Block ${blockKey} not found in this case`);
      setScanInput("");
      return;
    }

    // Check if already scanned (local state check for immediate feedback)
    const alreadyScanned = matchingBlocks.every((block: any) =>
      scannedBlocks.has(block.id)
    );
    if (alreadyScanned) {
      notifications.show({
        title: "Already Scanned",
        message: `Block ${blockKey} has already been scanned`,
        color: "blue",
      });
      setScanInput("");
      return;
    }

    // Call API to persist the scan
    try {
      const result = await markBlockAsEmbeddedMutation.mutateAsync({
        caseId,
        specimenNumber: specLetter,
        blockNumber: blockNum,
      });

      // Update local state
      const blockIdentity =
        result.block?.id ??
        matchingBlocks.find(
          (block: any) => !scannedBlocksRef.current.has(block.id)
        )?.id;
      if (blockIdentity) {
        markBlockScannedLocally(blockIdentity);
      }
      setScanInput("");

      if (result.block?.alreadyEmbedded) {
        notifications.show({
          title: "Already Embedded",
          message: `Block ${blockKey} was already embedded`,
          color: "blue",
        });
      } else {
        notifications.show({
          title: "Block Scanned",
          message: `Block ${blockKey} marked as embedded`,
          color: "green",
        });
      }

      if (result.embeddingCompleted) {
        autoCompletedRef.current = true;
        notifications.show({
          title: "Embedding Complete",
          message: "All blocks embedded — case moved to Pending Cutting",
          color: "green",
        });
      } else {
        await maybeAutoCompleteEmbedding();
      }
    } catch (error: any) {
      setScanError(error.message || "Failed to mark block as embedded");
      setScanInput("");
    }
  };

  // Handle manual block marking (click to mark)
  const handleManualBlockMark = async (specimen: any, block: any) => {
    // Skip if case is blocked
    if (caseData?.isBlocked) return;

    const blockKey = `${specimen.number}-${block.number}`;
    const blockId = block.id;

    // Skip if already marked
    if (!blockId) return;
    if (scannedBlocks.has(blockId)) return;

    try {
      const result = await markBlockAsEmbeddedMutation.mutateAsync({
        caseId: caseId!,
        specimenNumber: specimen.number,
        blockNumber: block.number,
      });

      // Update local state for immediate feedback
      markBlockScannedLocally(blockId);

      notifications.show({
        title: "Block Marked",
        message: `Block ${blockKey} marked as embedded`,
        color: "green",
      });
      if (result.embeddingCompleted) {
        autoCompletedRef.current = true;
        notifications.show({
          title: "Embedding Complete",
          message: "All blocks embedded — case moved to Pending Cutting",
          color: "green",
        });
      } else {
        await maybeAutoCompleteEmbedding();
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to mark block",
        color: "red",
      });
    }
  };

  useBarcodeScanner(
    useCallback(
      async (barcode: string) => {
        const parts = barcode.split(";");
        if (parts.length !== 3) return;
        if (!caseId) return;

        const [scannedCaseNumber, specLetter, blockNum] = parts;
        const isCurrentCase =
          caseData &&
          scannedCaseNumber.toUpperCase() === caseData.caseNumber.toUpperCase();

        if (isCurrentCase) {
          setScanInput(barcode);
          setScanError(null);
          const blockKey = `${specLetter}-${blockNum}`;
          const matchingBlocks = getMatchingBlocks(specLetter, blockNum);
          const blockExists = matchingBlocks.length > 0;

          if (!blockExists) {
            setScanError(`Block ${blockKey} not found in this case`);
            setScanInput("");
            return;
          }

          const alreadyScanned = matchingBlocks.every((block: any) =>
            scannedBlocksRef.current.has(block.id)
          );

          if (alreadyScanned || pendingScannedBlocksRef.current.has(blockKey)) {
            notifications.show({
              title: "Already Scanned",
              message: `Block ${blockKey} has already been scanned`,
              color: "blue",
            });
            setScanInput("");
            return;
          }

          // Track optimistically before awaiting so cross-case scans see the correct count
          pendingScannedBlocksRef.current = new Set([
            ...Array.from(pendingScannedBlocksRef.current),
            blockKey,
          ]);
          const mutationPromise = markBlockAsEmbeddedMutation.mutateAsync({
            caseId,
            specimenNumber: specLetter,
            blockNumber: blockNum,
          });
          pendingMutationPromisesRef.current = [
            ...pendingMutationPromisesRef.current,
            mutationPromise,
          ];

          try {
            const result = await mutationPromise;
            pendingScannedBlocksRef.current.delete(blockKey);
            pendingMutationPromisesRef.current =
              pendingMutationPromisesRef.current.filter(
                (p) => p !== mutationPromise
              );
            const blockIdentity =
              result.block?.id ??
              matchingBlocks.find(
                (block: any) => !scannedBlocksRef.current.has(block.id)
              )?.id;
            if (blockIdentity) {
              markBlockScannedLocally(blockIdentity);
            }
            setScanInput("");

            if (result.block?.alreadyEmbedded) {
              notifications.show({
                title: "Already Embedded",
                message: `Block ${blockKey} was already embedded`,
                color: "blue",
              });
            } else {
              notifications.show({
                title: "Block Scanned",
                message: `Block ${blockKey} marked as embedded`,
                color: "green",
              });
            }

            if (result.embeddingCompleted) {
              autoCompletedRef.current = true;
              notifications.show({
                title: "Embedding Complete",
                message: "All blocks embedded — case moved to Pending Cutting",
                color: "green",
              });
            } else {
              await maybeAutoCompleteEmbedding();
            }
          } catch (error: any) {
            pendingScannedBlocksRef.current.delete(blockKey);
            pendingMutationPromisesRef.current =
              pendingMutationPromisesRef.current.filter(
                (p) => p !== mutationPromise
              );
            setScanError(error.message || "Failed to mark block as embedded");
            setScanInput("");
          }
        } else {
          try {
            const completed = await maybeAutoCompleteEmbedding({
              silent: true,
            });
            if (!completed) {
              setScanError(
                "Finish scanning every block before opening another case"
              );
              return;
            }
            const caseDataRes = await openCaseByBarcodeMutation.mutateAsync({
              barcode,
              intent: "embedding",
            });
            navigate(
              `/embedding/${caseDataRes.id}?scannedSpec=${specLetter}&scannedBlock=${blockNum}`
            );
          } catch (err: any) {
            setScanError(err.message || "Failed to open case");
            setScanInput("");
          }
        }
      },
      [
        caseData,
        caseId,
        getMatchingBlocks,
        markBlockAsEmbeddedMutation,
        maybeAutoCompleteEmbedding,
        openCaseByBarcodeMutation,
        markBlockScannedLocally,
        navigate,
      ]
    ),
    {
      enabled: Boolean(
        caseId && caseData && !caseData.isBlocked && !isLockedByOther
      ),
      ownerKey: `embedding-detail:${caseId ?? "loading"}`,
      isCompleteBarcode: isCompleteCassetteBarcode,
    }
  );

  // Check if a specimen contains the scanned block
  const isScannedSpecimen = (specimen: any) => {
    if (!scannedSpec || !scannedBlock) return false;
    return (
      specimen.number === scannedSpec &&
      specimen.blocks?.some((b: any) => b.number === scannedBlock)
    );
  };

  // Loading state
  if (caseLoading) {
    return (
      <Box p="xl">
        <Group justify="center" p="xl">
          <Loader />
          <Text>Loading case...</Text>
        </Group>
      </Box>
    );
  }

  // Error state
  if (caseError || !caseData) {
    return (
      <Box p="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          Failed to load case data. Please try again.
        </Alert>
        <Button
          mt="md"
          onClick={() => navigate("/embedding")}
          leftSection={<IconArrowLeft size={16} />}
        >
          Back to Queue
        </Button>
      </Box>
    );
  }

  // Locked by another user state
  if (isLockedByOther) {
    return (
      <Box p="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="orange"
          title="Case Locked"
        >
          This case is currently being worked on by{" "}
          <strong>{lockData?.lockedByName || "another user"}</strong>. Please
          try again later or choose a different case from the queue.
        </Alert>
        <Group mt="md">
          <Button
            onClick={() => navigate("/embedding")}
            leftSection={<IconArrowLeft size={16} />}
          >
            Back to Queue
          </Button>
          {canForceRelease && (
            <Button
              color="red"
              variant="light"
              leftSection={<IconLockOpen size={16} />}
              onClick={handleForceUnlock}
              loading={forceReleaseMutation.isPending}
            >
              Force Unlock
            </Button>
          )}
        </Group>
      </Box>
    );
  }

  // Sort specimens alphabetically by number (A, B, C, etc.)
  const specimens = [...(caseData.specimens || [])].sort((a: any, b: any) =>
    (a.number || "").localeCompare(b.number || "")
  );
  const totalBlocks = specimens.reduce(
    (acc: number, s: any) => acc + Number(s.blocks?.length ?? 0),
    0
  );

  // Get all blocks for the case (from caseData.specimens[].blocks)
  const allBlocks = (caseData.specimens || []).flatMap(
    (specimen: any) => specimen.blocks || []
  );

  return (
    <>
      <style>{`
        .hide-scrollbar {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <Grid gutter={0} style={{ margin: "0 auto", maxWidth: "2200px" }}>
        {/* LEFT COLUMN: Case Summary, Notes, Errors */}
        <Grid.Col span={{ base: 12, lg: 2 }} style={{ minWidth: 300 }}>
          <Box
            style={{
              height: "calc(100vh - 76px)",
              position: "relative",
              borderRight: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            <Box
              style={{ height: "100%", overflowY: "auto" }}
              className="hide-scrollbar left-sidebar-scroll"
            >
              <Stack gap="md" p="md">
                {/* Case Summary - using GrossingCaseSummary component */}
                <GrossingCaseSummary
                  caseId={caseId}
                  caseNumber={caseData.caseNumber}
                  externalCaseNumber={caseData.externalCaseNumber}
                  patient={{
                    firstName: caseData.patient?.firstName,
                    lastName: caseData.patient?.lastName,
                    dateOfBirth: caseData.patient?.dateOfBirth,
                    mrn: caseData.patient?.medicalRecordNumber,
                  }}
                  procedure={caseData.procedure}
                  currentStatus={caseData.currentStatus}
                  isBlocked={caseData.isBlocked}
                  isRush={caseData.isRush}
                  isHighPriority={caseData.isHighPriority}
                  accessioningType={caseData.workflow?.accessioningType}
                  pathologist={{ fullName: caseData.pathologist?.fullName }}
                  facility={{ name: caseData.facility?.name }}
                  specimenCount={specimens.length}
                  blocksCount={totalBlocks}
                  hasPreviousCase={hasPreviousCase}
                  hasNextCase={hasNextCase}
                  onPreviousCase={handlePreviousCase}
                  onNextCase={handleNextCase}
                />

                <GrossingRequisitionCard key={caseId} caseId={caseId!} />

                {/* Case Flags */}
                <CaseFlagsSection caseId={caseId || null} />

                {/* Notes Section */}
                <CaseNotesSection caseId={caseId || null} />

                {/* Case Comments */}
                <CaseCommentsSection caseId={caseId || null} />

                {/* Errors Section */}
                <div data-errors-card>
                  <ErrorsCard caseId={caseId || null} />
                </div>
              </Stack>
            </Box>
            {/* Scroll indicator - only show when there's content to scroll */}
            {showScrollIndicator && (
              <Box
                onClick={() => {
                  const scrollContainer = document.querySelector(
                    ".left-sidebar-scroll"
                  );
                  if (scrollContainer) {
                    scrollContainer.scrollBy({
                      top: 150,
                      behavior: "smooth",
                    });
                  }
                }}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 36,
                  background:
                    "linear-gradient(to bottom, rgba(66, 133, 244, 0), rgba(66, 133, 244, 0.15))",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  paddingBottom: 6,
                }}
              >
                <Box
                  style={{
                    backgroundColor: "rgba(66, 133, 244, 0.2)",
                    borderRadius: 12,
                    padding: "2px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <IconChevronDown
                    size={14}
                    color="var(--mantine-color-blue-6)"
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Grid.Col>

        {/* MIDDLE COLUMN: Specimens Grid + Fixed Footer */}
        <Grid.Col span={{ base: 12, lg: "auto" }} style={{ flex: 1 }}>
          <Box
            style={{
              height: "calc(100vh - 76px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Scrollable Content Area */}
            <Box style={{ flex: 1, overflowY: "auto" }} p="md">
              <Stack gap="md">
                {/* Blocked Case Alert */}
                {caseData.isBlocked && (
                  <Alert
                    icon={<IconAlertTriangle size={16} />}
                    color="red"
                    title="Case Blocked"
                  >
                    <Text size="sm">
                      This case has been blocked due to a reported error.
                      Embedding cannot be completed until the error is resolved.{" "}
                      <Anchor
                        component="button"
                        type="button"
                        onClick={() => {
                          // Scroll to errors section in left column
                          const scrollContainer = document.querySelector(
                            ".left-sidebar-scroll"
                          );
                          const errorsSection =
                            document.querySelector("[data-errors-card]");
                          if (scrollContainer && errorsSection) {
                            const containerRect =
                              scrollContainer.getBoundingClientRect();
                            const errorRect =
                              errorsSection.getBoundingClientRect();
                            const scrollTop =
                              errorRect.top -
                              containerRect.top +
                              scrollContainer.scrollTop;
                            scrollContainer.scrollTo({
                              top: scrollTop,
                              behavior: "smooth",
                            });
                          }
                        }}
                        underline="always"
                      >
                        View errors
                      </Anchor>
                    </Text>
                  </Alert>
                )}

                {/* Received External Blocks */}
                {allBlocks.filter((b: any) => b.isCompleted).length > 0 && (
                  <Stack gap="xs">
                    <Text fw={500} size="sm" c="violet">
                      Received External Blocks
                    </Text>
                    {allBlocks
                      .filter((b: any) => b.isCompleted)
                      .map((block: any) => (
                        <ReceivedExternalPassThroughPanel
                          key={block.id}
                          itemLabel={block.blockLabel || block.id}
                          lastStickerPrintedAt={block.lastStickerPrintedAt}
                          onReprintClick={() => handleReprintSticker(block)}
                          onAcknowledge={() => {}}
                        />
                      ))}
                  </Stack>
                )}

                {/* Specimens Header */}
                <div>
                  <Text fw={500} size="lg">
                    Specimens & Blocks
                  </Text>
                  <Text size="sm" c="dimmed" maw={650}>
                    Verify all cassettes match the specimen details below.
                    <br />
                    Can&apos;t scan? Click any gray block badge (e.g.{" "}
                    <Badge
                      size="xs"
                      variant="light"
                      color="gray"
                      radius="sm"
                      styles={{
                        label: {
                          color: "var(--mantine-color-dark-9)",
                        },
                      }}
                    >
                      A-1
                    </Badge>
                    ) to mark it as embedded manually.
                  </Text>
                </div>

                {/* Specimens Grid */}
                <Grid gutter="md">
                  {specimens.map((specimen: any) => {
                    const isHighlighted = isScannedSpecimen(specimen);
                    const specimenBlocks = [...(specimen.blocks || [])].sort(
                      (a: any, b: any) =>
                        (a.number || "").localeCompare(b.number || "")
                    );
                    const allBlocksInSpecimenScanned =
                      specimenBlocks.length > 0 &&
                      specimenBlocks.every((block: any) =>
                        scannedBlocks.has(block.id)
                      );
                    const explicitFragmentCount = specimen.fragmentCount;
                    const explicitFragmentCountLabel = getFragmentCountLabel(
                      explicitFragmentCount,
                      specimen.fragmentCountQualifier
                    );
                    const extractedFragmentCount =
                      explicitFragmentCountLabel == null && specimen.gross
                        ? extractFragmentCountFromDescription(specimen.gross)
                        : null;
                    const displayFragmentCountLabel =
                      explicitFragmentCountLabel ??
                      getFragmentCountLabel(extractedFragmentCount, null);
                    const hasFragmentCount = displayFragmentCountLabel != null;
                    const isExtractedCount =
                      explicitFragmentCountLabel == null &&
                      extractedFragmentCount != null;
                    const isGrossExpanded = expandedGross.has(specimen.id);
                    const toggleGross = () =>
                      setExpandedGross((prev) => {
                        const next = new Set(prev);
                        if (next.has(specimen.id)) {
                          next.delete(specimen.id);
                        } else {
                          next.add(specimen.id);
                        }
                        return next;
                      });

                    return (
                      <Grid.Col key={specimen.id} span={{ base: 12, md: 6 }}>
                        <Card
                          padding="md"
                          withBorder
                          radius="md"
                          ref={isHighlighted ? highlightedSpecimenRef : null}
                          style={{
                            borderColor: allBlocksInSpecimenScanned
                              ? "var(--mantine-color-green-6)"
                              : isHighlighted
                              ? "var(--mantine-color-blue-6)"
                              : undefined,
                            borderWidth:
                              allBlocksInSpecimenScanned || isHighlighted
                                ? 2
                                : undefined,
                            backgroundColor: allBlocksInSpecimenScanned
                              ? "var(--mantine-color-green-0)"
                              : isHighlighted
                              ? "var(--mantine-color-blue-0)"
                              : undefined,
                            height: "100%",
                          }}
                        >
                          <Stack gap="sm">
                            {/* Compact Summary: Letter — Source — Fragment Count */}
                            <Group
                              justify="space-between"
                              align="center"
                              wrap="nowrap"
                            >
                              <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                                <Badge
                                  variant="filled"
                                  color={
                                    allBlocksInSpecimenScanned
                                      ? "green"
                                      : "cyan"
                                  }
                                  size="lg"
                                  radius="sm"
                                  style={{ flexShrink: 0 }}
                                >
                                  {specimen.number}
                                </Badge>
                                <Text fw={600} size="sm" truncate>
                                  {specimen.source?.name || "No source"}
                                  {specimen.subsite?.name &&
                                    ` (${specimen.subsite.name})`}
                                </Text>
                                <SpecimenInsufficientTissueCheckbox
                                  caseId={caseId!}
                                  specimen={specimen}
                                  stage="embedding"
                                  disabled={isLockedByOther}
                                />
                              </Group>
                              {hasFragmentCount && (
                                <Tooltip
                                  label={
                                    isExtractedCount
                                      ? "Extracted from gross description"
                                      : "Fragment count"
                                  }
                                  withArrow
                                >
                                  <Badge
                                    variant={
                                      isExtractedCount ? "light" : "filled"
                                    }
                                    color={
                                      isExtractedCount ? "orange" : "yellow.8"
                                    }
                                    size="lg"
                                    radius="sm"
                                    style={{ flexShrink: 0 }}
                                  >
                                    {displayFragmentCountLabel}
                                  </Badge>
                                </Tooltip>
                              )}
                            </Group>

                            {/* Block Badges Row */}
                            <Group
                              justify="space-between"
                              align="center"
                              wrap="wrap"
                            >
                              <Group gap="xs" wrap="wrap">
                                {specimenBlocks.map((block: any) => {
                                  const blockKey = `${specimen.number}-${block.number}`;
                                  const isBlockScanned = scannedBlocks.has(
                                    block.id
                                  );
                                  const canMark =
                                    !isBlockScanned && !caseData.isBlocked;

                                  return (
                                    <Tooltip
                                      key={block.id}
                                      label="Click to mark as embedded"
                                      disabled={
                                        isBlockScanned || caseData.isBlocked
                                      }
                                      withArrow
                                      position="top"
                                    >
                                      <Badge
                                        size="md"
                                        variant={
                                          isBlockScanned ? "filled" : "light"
                                        }
                                        color={
                                          isBlockScanned ? "green" : "gray"
                                        }
                                        leftSection={
                                          isBlockScanned ? (
                                            <IconCheck size={12} />
                                          ) : null
                                        }
                                        radius="sm"
                                        style={{
                                          cursor: canMark
                                            ? "pointer"
                                            : "default",
                                        }}
                                        styles={{
                                          label: {
                                            color: !isBlockScanned
                                              ? "var(--mantine-color-dark-9)"
                                              : undefined,
                                          },
                                        }}
                                        onClick={() =>
                                          canMark &&
                                          handleManualBlockMark(specimen, block)
                                        }
                                      >
                                        {blockKey}
                                      </Badge>
                                    </Tooltip>
                                  );
                                })}
                              </Group>
                              {allBlocksInSpecimenScanned && (
                                <Badge
                                  color="green"
                                  leftSection={<IconCheck size={12} />}
                                  radius="sm"
                                >
                                  All Scanned
                                </Badge>
                              )}
                            </Group>

                            {/* Collapsible Gross Description */}
                            {specimen.gross && (
                              <>
                                <Divider size="xs" />
                                <Group justify="space-between" align="center">
                                  <UnstyledButton
                                    onClick={toggleGross}
                                    style={{ flex: 1 }}
                                  >
                                    <Group gap={4}>
                                      {isGrossExpanded ? (
                                        <IconChevronDown size={14} />
                                      ) : (
                                        <IconChevronRight size={14} />
                                      )}
                                      <Text
                                        size="xs"
                                        c="dimmed"
                                        fw={600}
                                        tt="uppercase"
                                        style={{ letterSpacing: "0.5px" }}
                                      >
                                        Gross Description
                                      </Text>
                                    </Group>
                                  </UnstyledButton>
                                  {[
                                    "Pending Tissue Processing",
                                    "Embedding",
                                  ].includes(caseData.currentStatus?.name) && (
                                    <Tooltip
                                      label="Edit grossing description"
                                      withArrow
                                    >
                                      <ActionIcon
                                        variant="subtle"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingSpecimen(specimen);
                                          openGrossEditor();
                                        }}
                                      >
                                        <IconEdit size={14} />
                                      </ActionIcon>
                                    </Tooltip>
                                  )}
                                </Group>
                                <Collapse in={isGrossExpanded}>
                                  <Paper
                                    p="sm"
                                    bg="gray.0"
                                    radius="sm"
                                    style={{
                                      maxHeight: 150,
                                      overflowY: "auto",
                                    }}
                                  >
                                    {/* eslint-disable react/no-danger */}
                                    <div
                                      style={{
                                        fontSize: "var(--mantine-font-size-sm)",
                                        lineHeight: 1.5,
                                        color: "var(--mantine-color-gray-8)",
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: resolveGrossVariables(
                                          specimen.gross
                                        ),
                                      }}
                                    />
                                  </Paper>
                                </Collapse>
                              </>
                            )}
                          </Stack>
                        </Card>
                      </Grid.Col>
                    );
                  })}
                </Grid>
              </Stack>
            </Box>

            {/* Fixed Footer */}
            <Box
              style={{
                borderTop: "1px solid var(--mantine-color-gray-3)",
                padding: "var(--mantine-spacing-md)",
                backgroundColor: "white",
              }}
            >
              <Group justify="space-between">
                <Button
                  variant="outline"
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={openCancelModal}
                >
                  Back to Queue
                </Button>
                <Group gap="sm">
                  {!caseData.isBlocked && (
                    <>
                      <Tooltip
                        label={
                          scannedBlocks.size < totalBlocks
                            ? `Scan all ${totalBlocks} blocks before completing`
                            : ""
                        }
                        disabled={scannedBlocks.size === totalBlocks}
                      >
                        <Button
                          color="green"
                          onClick={handleCompleteEmbedding}
                          loading={completeEmbeddingMutation.isPending}
                          disabled={scannedBlocks.size < totalBlocks}
                        >
                          Complete Embedding
                          {totalBlocks > 0 &&
                            ` (${scannedBlocks.size}/${totalBlocks})`}
                        </Button>
                      </Tooltip>
                      {nextCase && (
                        <Tooltip
                          label={
                            scannedBlocks.size < totalBlocks
                              ? `Scan all ${totalBlocks} blocks before completing`
                              : `Next: ${nextCase.caseNumber}`
                          }
                        >
                          <Button
                            variant="light"
                            color="green"
                            leftSection={<IconPlayerTrackNext size={16} />}
                            onClick={handleCompleteAndNext}
                            loading={completeEmbeddingMutation.isPending}
                            disabled={scannedBlocks.size < totalBlocks}
                          >
                            Complete & Next
                          </Button>
                        </Tooltip>
                      )}
                    </>
                  )}
                </Group>
              </Group>
            </Box>
          </Box>
        </Grid.Col>

        {/* RIGHT COLUMN: Scanner */}
        <Grid.Col span={{ base: 12, lg: 2 }} style={{ minWidth: 320 }}>
          <Box
            style={{
              height: "calc(100vh - 76px)",
              display: "flex",
              flexDirection: "column",
              borderLeft: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            <Box style={{ flex: 1, overflowY: "auto" }}>
              <Stack gap="md" p="md">
                {/* Sticker Printer Status */}
                <StickerPrinterStatusPanel />

                {/* Block Scanning Section */}
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    {showLegacyScannerControls && (
                      <Group gap="xs">
                        <IconBarcode size={20} />
                        <Text size="lg" fw={500}>
                          Scan Block
                        </Text>
                      </Group>
                    )}

                    {caseData.isBlocked ? (
                      <Alert color="orange" variant="light">
                        <Text size="sm">
                          Scanning is disabled while the case is blocked.
                        </Text>
                      </Alert>
                    ) : (
                      <>
                        {showLegacyScannerControls && (
                          <>
                            <TextInput
                              ref={scanInputRef}
                              placeholder="Scan cassette barcode..."
                              value={scanInput}
                              onChange={(e) => {
                                setScanInput(e.target.value);
                                setScanError(null);
                              }}
                              onKeyDown={handleBlockScan}
                              size="md"
                              error={scanError}
                              autoFocus
                            />

                            <Divider />
                          </>
                        )}

                        <div>
                          <Text fw={500} size="sm" mb="xs">
                            Scan Progress
                          </Text>
                          <Progress
                            value={
                              totalBlocks > 0
                                ? (scannedBlocks.size / totalBlocks) * 100
                                : 0
                            }
                            color={
                              scannedBlocks.size === totalBlocks
                                ? "green"
                                : "blue"
                            }
                            size="lg"
                            mb="xs"
                          />
                          <Text size="sm" c="dimmed" ta="center">
                            {scannedBlocks.size} of {totalBlocks} blocks scanned
                          </Text>
                          <Text
                            size="xs"
                            c="dimmed"
                            ta="center"
                            mt="xs"
                            fs="italic"
                          >
                            Tip: Click a block badge to mark manually
                          </Text>
                        </div>

                        {scannedBlocks.size === totalBlocks &&
                          totalBlocks > 0 && (
                            <Badge
                              color="green"
                              size="lg"
                              fullWidth
                              variant="light"
                              leftSection={<IconCheck size={14} />}
                            >
                              All blocks scanned
                            </Badge>
                          )}
                      </>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Box>
          </Box>
        </Grid.Col>
      </Grid>

      {/* Leave Confirmation Modal */}
      <Modal
        opened={cancelModalOpened}
        onClose={closeCancelModal}
        title="Leave Embedding?"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to leave embedding for case{" "}
            {caseData.caseNumber}? The case lock will be released.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="outline" onClick={closeCancelModal}>
              Stay
            </Button>
            <Button
              color="red"
              onClick={handleCancel}
              loading={releaseLockMutation.isPending}
            >
              Leave
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Gross Description Editor Modal */}
      {editingSpecimen && (
        <GrossDescriptionEditorModal
          opened={grossEditorOpened}
          onClose={() => {
            closeGrossEditor();
            setEditingSpecimen(null);
          }}
          caseId={caseId!}
          specimen={editingSpecimen}
          onSaveSuccess={() => {
            // Refetch case data to reflect updated gross description
            refetchCase();
          }}
        />
      )}
    </>
  );
}
